import React, { useState } from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import { getAdminNotifications, getNotificationTitleStatAdmin, getNotificationTitlesAdmin } from '../../graphql/queries';
import { Table, Card, Input, Tag, Space, Tooltip, Button, Switch, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Notifications.css';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import NotificationChart from './NotificationChart';

const { Search } = Input;
const { RangePicker } = DatePicker;

const Notifications = () => {
  const navigate = useNavigate();
  const [rcUserId, setRcUserId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [type, setType] = useState('ALL');
  const [daysBack, setDaysBack] = useState(30);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [statsModalOpen, setStatsModalOpen] = useState(false);

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);

  const startDate = dateRange[0].toISOString();
  const endDate = dateRange[1].toISOString();

  const { loading, error, data, refetch } = useQuery(getAdminNotifications, {
    variables: {
      rcUserId: rcUserId || null,
      page,
      pageSize,
      unreadOnly,
      type: type === 'ALL' ? null : type,
      daysBack,
    },
  });

  // Only fetch stats when the modal is open AND a title has been picked.
  // Previously this fired on every page mount with title=null (a required arg
  // on the backend), producing a wasted request + validation error each load.
  const {
    data: statsData,
    loading: statsLoading,
  } = useQuery(getNotificationTitleStatAdmin, {
    variables: {
      startDate,
      endDate,
      title: selectedTitle,
    },
    skip: !statsModalOpen || !selectedTitle,
    fetchPolicy: 'network-only',
  });

  // The titles list is only needed inside the stats modal - don't hit the
  // (expensive) distinct-titles query on every page load.
  const {
    data: titlesData,
    loading: titlesLoading,
  } = useQuery(getNotificationTitlesAdmin, {
    skip: !statsModalOpen,
  });

  const notificationTypeOptions = [
    { value: 'ALL', label: 'All Types' },
    { value: 'LIKE', label: 'LIKE' },
    { value: 'MILESTONE_10', label: 'MILESTONE_10' },
    { value: 'MILESTONE_25', label: 'MILESTONE_25' },
    { value: 'MILESTONE_50', label: 'MILESTONE_50' },
    { value: 'MILESTONE_100', label: 'MILESTONE_100' },
    { value: 'FEED_CONFIRMED', label: 'FEED_CONFIRMED' },
    { value: 'VIDEO_COMPLETED', label: 'VIDEO_COMPLETED' },
    { value: 'VIDEO_FAILED', label: 'VIDEO_FAILED' },
    { value: 'GENERATION_DELAYED', label: 'GENERATION_DELAYED' },
    { value: 'GENERATION_TIMEOUT', label: 'GENERATION_TIMEOUT' },
    { value: 'CREDIT_RECEIVED', label: 'CREDIT_RECEIVED' },
    { value: 'DAILY_STREAK', label: 'DAILY_STREAK' },
    { value: 'PROMPT_COPY', label: 'PROMPT_COPY' },
    { value: 'CANCELLED_BY_USER', label: 'CANCELLED_BY_USER' },
    { value: 'OTHER', label: 'OTHER' },
  ];

  const handleSearch = (value) => {
    setRcUserId(value);
    setSearchValue(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setRcUserId('');
    setSearchValue('');
    setPage(1);
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      LIKE: 'blue',
      MILESTONE_10: 'green',
      MILESTONE_25: 'green',
      MILESTONE_50: 'gold',
      MILESTONE_100: 'red',
      FEED_CONFIRMED: 'cyan',
      VIDEO_COMPLETED: 'green',
      VIDEO_FAILED: 'red',
      CREDIT_RECEIVED: 'purple',
      DAILY_STREAK: 'orange',
      PROMPT_COPY: 'magenta',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  // NOTE: column-level `filters`/`onFilter` were removed - they only filtered the
  // 20 rows currently loaded on the page (client-side), which was misleading next
  // to the server-side Type select and Unread switch that filter the real dataset.
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color={getNotificationTypeColor(type)}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <div className="user-cell">
          <Tooltip title="Click to view user details">
            <Button
              type="link"
              onClick={() => navigate(`/user/${record.userId}`)}
              style={{ padding: 0 }}
            >
              {record.user?.rcUserId || record.userId}
            </Button>
          </Tooltip>
          {record.user?.email && (
            <div className="user-email">{record.user.email}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip placement="topLeft" title={title}>
          {title}
        </Tooltip>
      ),
    },
    {
      title: 'Body',
      dataIndex: 'body',
      key: 'body',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (body) => (
        <Tooltip placement="topLeft" title={body}>
          {body}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 100,
      render: (isRead) => (
        <Tag color={isRead ? 'default' : 'blue'}>
          {isRead ? 'Read' : 'Unread'}
        </Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
    },
    {
      title: 'Read At',
      dataIndex: 'readAt',
      key: 'readAt',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }) : '-',
    },
  ];

  const notifications = data?.getAdminNotifications?.notifications || [];
  const totalCount = data?.getAdminNotifications?.totalCount || 0;
  const COUNT_CAP = 10000;

  return (
    <div className="notifications-container">
      <Card
        title={
          <Space>
            <span>Notifications</span>
          </Space>
        }
        extra={
          <Space>
            <span>Show Unread Only:</span>
            <Switch
              checked={unreadOnly}
              onChange={(checked) => {
                setUnreadOnly(checked);
                setPage(1);
              }}
            />
            <Select
              value={type}
              onChange={(value) => {
                setType(value);
                setPage(1);
              }}
              style={{ minWidth: 150 }}
              options={notificationTypeOptions}
            />
            {!rcUserId && (
              <Select
                value={daysBack}
                onChange={(value) => {
                  setDaysBack(value);
                  setPage(1);
                }}
                style={{ minWidth: 140 }}
                options={[
                  { value: 30, label: 'Last 30 days' },
                  { value: 90, label: 'Last 90 days' },
                ]}
              />
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="default"
              onClick={() => setStatsModalOpen(true)}
            >
              Show stats by title
            </Button>

          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div className="search-section">
            <Search
              placeholder="Search by RevenueCat User ID"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ maxWidth: 400 }}
            />
            {rcUserId && (
              <Button onClick={handleClearSearch} style={{ marginLeft: 8 }}>
                Clear Search
              </Button>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={notifications}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              // Backend caps the count at COUNT_CAP to keep the query cheap;
              // make it clear the total is "10000+" rather than exact.
              showTotal: (total) =>
                total >= COUNT_CAP ? `${COUNT_CAP.toLocaleString()}+ results` : `${total} results`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              },
            }}
            scroll={{ x: 'max-content' }}
            rowClassName={(record) => !record.isRead ? 'unread-row' : ''}
          />
        </Space>
      </Card>
      <Modal
        title="Notification stats by title"
        open={statsModalOpen}
        onCancel={() => setStatsModalOpen(false)}
        footer={null}
        width={700}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>

          {/* TITLE SELECT */}
          <Select
            value={selectedTitle}
            onChange={setSelectedTitle}
            loading={titlesLoading}
            placeholder="Select notification title"
            style={{ width: '100%' }}
            showSearch
          >
            {(titlesData?.getNotificationTitlesAdmin || []).map((title) => (
              <Select.Option key={title} value={title}>
                {title}
              </Select.Option>
            ))}
          </Select>

          {/* DATE RANGE */}
          <RangePicker
            value={dateRange}
            onChange={(values) => {
              if (!values) return;
              setDateRange([
                values[0].startOf('day'),
                values[1].endOf('day'),
              ]);
            }}
            allowClear={false}
            style={{ width: '100%' }}
          />

          {/* CHART */}
          <NotificationChart
            stats={statsData?.getNotificationTitleStatAdmin}
            loading={statsLoading}
          />
        </Space>
      </Modal>

    </div>
  );
};

export default Notifications;